import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const dayKeys = ["hours.monday", "hours.tuesday", "hours.wednesday", "hours.thursday", "hours.friday", "hours.saturday", "hours.sunday"];

const OpeningHoursSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [hours, setHours] = useState(
    dayKeys.map((key, i) => ({ key, open: i < 5, from: "09:00", to: "17:00", id: null as string | null }))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!profile?.organisation_id) return;
      const { data } = await supabase.from("opening_hours").select("*").eq("organisation_id", profile.organisation_id).order("day_of_week");
      if (data && data.length > 0) {
        setHours(dayKeys.map((key, i) => {
          const row = data.find((d: any) => d.day_of_week === i);
          return { key, open: row?.is_open ?? i < 5, from: row?.open_time?.slice(0, 5) ?? "09:00", to: row?.close_time?.slice(0, 5) ?? "17:00", id: row?.id ?? null };
        }));
      }
      setLoading(false);
    };
    fetch();
  }, [profile?.organisation_id]);

  const toggleDay = (index: number, open: boolean) => { setHours((prev) => prev.map((h, i) => (i === index ? { ...h, open } : h))); };
  const updateTime = (index: number, field: "from" | "to", value: string) => { setHours((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))); };

  const handleSave = async () => {
    if (!profile?.organisation_id) return;
    setSaving(true);
    try {
      for (let i = 0; i < hours.length; i++) {
        const h = hours[i];
        const payload = { organisation_id: profile.organisation_id, day_of_week: i, is_open: h.open, open_time: h.from, close_time: h.to };
        if (h.id) { await supabase.from("opening_hours").update(payload).eq("id", h.id); }
        else { await supabase.from("opening_hours").insert(payload); }
      }
      toast.success(t("hours.saved"));
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center p-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>);
  }

  return (
    <>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("hours.title")}</h1>
      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <div className="space-y-3">
          {hours.map((h, i) => (
            <div key={h.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="w-full sm:w-28 flex items-center justify-between sm:justify-start">
                <span className="text-sm font-medium text-foreground">{t(h.key)}</span>
                <div className="sm:hidden">
                  <Switch checked={h.open} onCheckedChange={(v) => toggleDay(i, v)} />
                </div>
              </div>
              <div className="hidden sm:block">
                <Switch checked={h.open} onCheckedChange={(v) => toggleDay(i, v)} />
              </div>
              {h.open ? (
                <div className="flex items-center gap-2">
                  <Input className="flex-1 sm:w-24 sm:flex-none" type="time" value={h.from} onChange={(e) => updateTime(i, "from", e.target.value)} />
                  <span className="text-sm text-muted-foreground">{t("hours.to")}</span>
                  <Input className="flex-1 sm:w-24 sm:flex-none" type="time" value={h.to} onChange={(e) => updateTime(i, "to", e.target.value)} />
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">{t("hours.closed")}</span>
              )}
            </div>
          ))}
        </div>
        <Button className="mt-4" onClick={handleSave} disabled={saving}>{saving ? t("hours.saving") : t("hours.save")}</Button>
      </section>
    </>
  );
};

export default OpeningHoursSettings;
